import { MutationCache, QueryCache, QueryClient, DefaultOptions } from "react-query"
import { ErrorDescription } from "@kiwicom/openapi-sdk/mmb"

const isUnauthenticated = (error: Response | ErrorDescription): boolean =>
  error instanceof Response && error.status === 401

const is404 = (error: Response | ErrorDescription): boolean =>
  error instanceof Response && error.status === 404

/**
 * Tells whenever retry is allowed or not
 *
 * @param failureCount - retries counted so far
 * @param error - Error {@link Response} | {@link ErrorDescription}
 * @returns no retries for 404 or unauthenticated user (401), max 3 retries for other failed responses
 */
export const shouldRetry = (failureCount: number, error: Response | ErrorDescription): boolean =>
  !isUnauthenticated(error) && !is404(error) && failureCount < 3

/**
 * Global React Query Cache
 *
 * DO NOT USE unless:
 * 1. You are solving MMB platform level problem (affecting all MMB modules)
 * 2. You can't use useQueryClient hook
 */
export const queryCache = new QueryCache()

/**
 * Creates handler for invalidating every query in the cache after successful mutation. This is to make sure that
 * we always display the latest data to the user when navigating through the MMB pages.
 *
 * More info about this feature can be found here:
 * https://docs.google.com/document/d/1TaKGPO3BFE7lWPxIk_NfkM6p1s5jdZWZ19BryxCo8Qo
 *
 * @returns function to be passed as onSuccess callback
 */
const createDefaultMutationSuccessHandler = () => (): void => {
  const queries = queryCache.findAll()
  queries.forEach((query) => {
    query.invalidate()
  })
}

/**
 * Global React Mutation Cache
 *
 * DO NOT USE unless:
 * 1. You are solving MMB platform level problem (affecting all MMB modules)
 * 2. You can't use useQueryClient hook
 */
export const mutationCache = new MutationCache({
  onSuccess: createDefaultMutationSuccessHandler(),
})

/**
 * It is recommended to overwrite default error
 * @param router - next router
 *
 * @returns QueryClient - query client
 */
export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        // to avoid data re-fetching when changing routes.
        staleTime: 10 * 60 * 1000, // 10 min
        retry: shouldRetry,
      },
    } as DefaultOptions,
    queryCache,
    mutationCache,
  })
}

export const queryClient = createQueryClient()
